import useAuth from '@State/Auth/useAuth';

type HttpRequestMethod = 'GET' | 'POST' | 'PUT' | 'DELETE';
type contentTypes = 'application/json' | 'application/csv' | 'application/xlsx';

/**
 * Ett svar som inte var ok. Statusen följer med så att en sida kan skilja
 * nekad åtkomst (403) från andra fel, och svarskroppen behålls för den som
 * behöver API:ets egen felkod.
 */
export class HttpError extends Error {
  readonly status: number;
  readonly body: unknown;

  constructor(response: Response, body: unknown) {
    super(`Fetch ${response.status} Error: ${response.statusText}`);
    this.name = 'HttpError';
    this.status = response.status;
    this.body = body;
  }
}

/** API:et nekade kontot åtkomst till det som efterfrågades */
export const isForbidden = (error: unknown): boolean =>
  error instanceof HttpError && error.status === 403;

/**
 * API:ets egen felkod (`errorCode` i svarskroppen), när svaret bar en sådan.
 * Statusen räcker inte alltid: samma 500 kan betyda olika saker.
 */
export const getApiErrorCode = (error: unknown): string | undefined => {
  if (!(error instanceof HttpError) || typeof error.body !== 'object' || error.body === null) {
    return undefined;
  }
  const { errorCode } = error.body as { errorCode?: unknown };
  return typeof errorCode === 'string' ? errorCode : undefined;
};

async function handleErrors(response: Response) {
  if (!response.ok) {
    const body = response.headers.get('content-type')?.includes('application/json')
      ? await response.json().catch(() => undefined)
      : undefined;

    throw new HttpError(response, body);
  }

  return response;
}

export const useFetcher = () => {
  const { getAccessToken } = useAuth();

  const baseUrl = import.meta.env.VITE_CUSTOMER_PORTAL_API;

  type FetcherOptions = {
    body?: any;
    method?: HttpRequestMethod;
    headers?: RequestInit['headers'];
    accept?: contentTypes;
    contentType?: contentTypes;
    isFormData?: boolean;
  };

  async function fetchWithToken(
    url: string,
    { method = 'GET', body, accept, contentType, headers }: FetcherOptions = {}
  ) {
    const token = await getAccessToken();

    const options: RequestInit = {
      method: method || 'GET',
      headers: {
        Channel: 'PORTAL',
        ...(headers && headers),
        authorization: `Bearer ${token}`,
        ...(accept && { Accept: accept }),
        ...{ ['Content-Type']: 'application/json' },
        ...(contentType && { ['Content-Type']: contentType }),
      },
      ...(body && { body: JSON.stringify(body) }),
    };

    if (url.startsWith('/')) {
      url = `${baseUrl}${url}`;
    }

    // We catch and log the error as to not error out on empty response bodies
    return fetch(url, options)
      .then((res) => handleErrors(res))
      .then((res) => {
        if (res.headers.get('content-type')?.includes('application/json')) {
          if (res.headers.get('content-length') === '0') {
            return res.text();
          }
          return res.json();
        }
        return res;
      });
  }

  async function fetchWithoutToken(
    url: string,
    { method = 'GET', body, isFormData = false }: FetcherOptions = {}
  ) {
    const options: RequestInit = isFormData
      ? {
          method,
          headers: {
            Channel: 'PORTAL',
          },
          body,
          contentType: 'multipart/form-data',
        }
      : {
          method,
          headers: {
            Channel: 'PORTAL',
            ...(body && { ['Content-Type']: 'application/json' }),
          },
          body,
          ...(body && { body: JSON.stringify(body) }),
        };

    if (url.startsWith('/')) {
      url = `${baseUrl}${url}`;
    }

    // We catch and log the error as to not error out on empty response bodies
    return fetch(url, options)
      .then((res) => handleErrors(res))
      .then((res) => {
        if (
          res.headers
            .get('content-type')
            ?.includes(isFormData ? 'multipart/form-data' : 'application/json')
        ) {
          if (res.headers.get('content-length') === '0') {
            return res.text();
          }
          return res.json();
        }
      });
  }

  return {
    fetchWithToken,
    fetchWithoutToken,
  };
};
